import React, { useState, useEffect } from 'react';
import {
  HiPlus,
  HiTrash,
  HiX,
  HiSearch,
  HiDocument,
  HiDocumentText,
  HiDownload,
  HiEye
} from 'react-icons/hi';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import './Documents.css';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    tipo: 'nota_fiscal',
    descricao: '',
    arquivo: null, // Armazenar o objeto File original
    preview: null // Para preview (base64)
  });
  const [viewDocument, setViewDocument] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const data = await apiService.getDocuments();
      const documentsList = normalizeApiData(data);
      setDocuments(documentsList);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      setDocuments([]);
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Criar preview para exibição
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          arquivo: file, // Armazenar o objeto File original para envio binário
          preview: reader.result // Preview em base64 para exibição
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.arquivo || !formData.descricao) {
      showWarning('Preencha todos os campos e selecione um arquivo');
      return;
    }

    try {
      // Garantir que temos um objeto File para envio binário
      if (!(formData.arquivo instanceof File)) {
        showWarning('Por favor, selecione um arquivo válido');
        return;
      }

      await apiService.createDocument({
        tipo: formData.tipo,
        descricao: formData.descricao,
        arquivo: formData.arquivo, // Enviar o objeto File original como binário
      });

    handleCloseModal();
    showSuccess('Documento adicionado com sucesso!');
      loadDocuments();
    } catch (error) {
      console.error('Erro ao criar documento:', error);
      showWarning(error.message || 'Erro ao criar documento');
    }
  };

  const handleDelete = (id) => {
    setDocumentToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteDocument(documentToDelete);
    setShowConfirmDialog(false);
    setDocumentToDelete(null);
    showSuccess('Documento excluído com sucesso!');
      loadDocuments();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      showWarning(error.message || 'Erro ao excluir documento');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      tipo: 'nota_fiscal',
      descricao: '',
      arquivo: null,
      preview: null
    });
  };

  const getTipoLabel = (tipo) => {
    if (!tipo) return 'Outros';
    const tipos = {
      nota_fiscal: 'Nota Fiscal',
      cupom_fiscal: 'Cupom Fiscal',
      invoice: 'Invoice',
      contrato: 'Contrato',
      outros: 'Outros'
    };
    return tipos[tipo] || tipo || 'Outros';
  };

  const getTipoBadge = (tipo) => {
    const badges = {
      nota_fiscal: { label: 'Nota Fiscal', class: 'badge-primary' },
      cupom_fiscal: { label: 'Cupom Fiscal', class: 'badge-info' },
      invoice: { label: 'Invoice', class: 'badge-success' },
      contrato: { label: 'Contrato', class: 'badge-warning' },
      outros: { label: 'Outros', class: 'badge-secondary' }
    };
    const badge = badges[tipo] || badges.outros;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await apiService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Detectar extensão do arquivo baseado no tipo MIME do blob
      let extension = 'bin';
      if (blob.type) {
        const mimeToExt = {
          'application/pdf': 'pdf',
          'image/png': 'png',
          'image/jpeg': 'jpg',
          'image/jpg': 'jpg',
          'image/gif': 'gif',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        };
        extension = mimeToExt[blob.type] || blob.type.split('/')[1] || 'bin';
      } else if (doc.arquivo_url) {
        // Tentar extrair extensão da URL do arquivo
        const urlMatch = doc.arquivo_url.match(/\.([a-zA-Z0-9]+)(\?|$)/);
        if (urlMatch) {
          extension = urlMatch[1];
        }
      }

      // Usar nome_arquivo se disponível, caso contrário criar nome baseado na descrição
      const fileName = doc.nome_arquivo || `${doc.descricao || 'documento'}_${doc.created_at ? new Date(doc.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}.${extension}`;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      showWarning(error.message || 'Erro ao baixar documento');
    }
  };

  const filteredDocuments = Array.isArray(documents) ? documents.filter(doc => {
    const descricao = (doc.descricao || '').toLowerCase();
    const tipoLabel = getTipoLabel(doc.tipo || '').toLowerCase();
    const search = (searchTerm || '').toLowerCase();
    return descricao.includes(search) || tipoLabel.includes(search);
  }) : [];

  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Documentos</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Documentos</h1>
          <p>Gerencie notas fiscais, invoices e outros documentos</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <HiPlus size={20} />
          Novo Documento
        </button>
      </div>

      <div className="page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={6} />
        ) : paginatedDocuments.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'Nenhum documento encontrado' : 'Nenhum documento cadastrado'}</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tipo</th>
                    <th>Descrição</th>
                    <th>Data</th>
                    <th>Tamanho</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocuments.map((doc, index) => (
                    <tr key={doc.id}>
                      <td>{startIndex + index + 1}</td>
                      <td>{getTipoBadge(doc.tipo)}</td>
                      <td><strong>{doc.descricao}</strong></td>
                      <td>
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleString('pt-BR', {
                              timeZone: 'America/Sao_Paulo',
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })
                          : (doc.data
                              ? new Date(doc.data).toLocaleString('pt-BR', {
                                  timeZone: 'America/Sao_Paulo',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })
                              : '-')}
                      </td>
                      <td>
                        {doc.tamanho
                          ? (() => {
                              const tamanhoBytes = typeof doc.tamanho === 'number' ? doc.tamanho : parseFloat(doc.tamanho);
                              if (tamanhoBytes >= 1048576) {
                                // Maior ou igual a 1 MB
                                return `${(tamanhoBytes / 1048576).toFixed(2)} MB`;
                              } else {
                                // Menor que 1 MB, mostrar em KB
                                return `${(tamanhoBytes / 1024).toFixed(2)} KB`;
                              }
                            })()
                          : '-'}
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn-icon btn-view"
                            onClick={async () => {
                              try {
                                const blob = await apiService.getDocument(doc.id);
                                const url = window.URL.createObjectURL(blob);
                                setViewDocument({ ...doc, arquivo: url });
                              } catch (error) {
                                console.error('Erro ao visualizar documento:', error);
                                showWarning(error.message || 'Erro ao visualizar documento');
                              }
                            }}
                            title="Visualizar"
                          >
                            <HiEye size={18} />
                          </button>
                          <button
                            className="btn-icon btn-download"
                            onClick={() => handleDownload(doc)}
                            title="Download"
                          >
                            <HiDownload size={18} />
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(doc.id)}
                            title="Excluir"
                          >
                            <HiTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {filteredDocuments.length > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, filteredDocuments.length)} de {filteredDocuments.length} registro{filteredDocuments.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de Upload */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Novo Documento</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Tipo de Documento</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  required
                >
                  <option value="nota_fiscal">Nota Fiscal</option>
                  <option value="cupom_fiscal">Cupom Fiscal</option>
                  <option value="invoice">Invoice</option>
                  <option value="contrato">Contrato</option>
                  <option value="outros">Outros</option>
                </select>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  placeholder="Ex: Nota Fiscal PKG001"
                />
              </div>
              <div className="form-group">
                <label>Arquivo</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    id="document-file"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="document-file" className="file-upload-btn">
                    <HiDocument size={24} />
                    <span>Selecionar Arquivo</span>
                    <small>PNG, JPG, PDF (máx. 10MB)</small>
                  </label>
                  {formData.preview && (
                    <div className="file-preview">
                      {formData.preview.includes('data:image') ? (
                        <img src={formData.preview} alt="Preview" />
                      ) : (
                        <div className="pdf-preview">
                          <HiDocumentText size={48} />
                          <span>PDF Selecionado</span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="remove-file"
                        onClick={() => setFormData({ ...formData, arquivo: null, preview: null })}
                      >
                        <HiX size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Salvar Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {viewDocument && (
        <div className="modal-overlay" onClick={() => setViewDocument(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewDocument.descricao}</h2>
              <button className="modal-close" onClick={() => setViewDocument(null)}>
                <HiX size={24} />
              </button>
            </div>
            <div className="document-viewer">
              {viewDocument.arquivo.includes('data:image') ? (
                <img src={viewDocument.arquivo} alt={viewDocument.descricao} />
              ) : (
                <iframe
                  src={viewDocument.arquivo}
                  title={viewDocument.descricao}
                  style={{ width: '100%', height: '600px', border: 'none' }}
                />
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewDocument(null)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleDownload(viewDocument)}
              >
                <HiDownload size={18} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este documento?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setDocumentToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Documents;
